import React, { useEffect, useState } from "react";
import { Button, Modal, Form, Input, Table } from "antd";
import PageHeader from "../../components/PageHeader";
import Footer from "../../components/Footer";
import Breadcrumb from "../../components/Breadcrumb";
import {
  PageWrapper,
  DashboardMain,
  DashboardSection,
  ListHeader,
  ModuleCardTitle,
} from "./styles";
import { useContent, FaqItem } from "../../hooks/content";
import useAdminGuard from "../../hooks/useAdminGuard";

const FAQ: React.FC = () => {
  useAdminGuard();
  const { listFaq, saveFaq, removeFaq } = useContent();
  const [items, setItems] = useState<FaqItem[]>([]);
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState<FaqItem | null>(null);
  const [form] = Form.useForm();

  const load = React.useCallback(async () => {
    try {
      const data = await listFaq();
      setItems(data);
    } catch (error) {
      console.error(error);
    }
  }, [listFaq]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setVisible(true);
  };

  const openEdit = (item: FaqItem) => {
    setEditing(item);
    form.setFieldsValue({
      title: item.title,
      description: item.description,
      answer: item.answer,
    });
    setVisible(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    await saveFaq({
      uid: editing?.uid,
      title: values.title,
      description: values.description,
      answer: values.answer,
    });
    setVisible(false);
    await load();
  };

  const handleRemove = async (uid: string) => {
    await removeFaq(uid);
    await load();
  };

  const columns = [
    {
      title: "Pergunta",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Descrição",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Resposta",
      dataIndex: "answer",
      key: "answer",
      ellipsis: true,
    },
    {
      title: "Ações",
      key: "actions",
      render: (_: any, record: FaqItem) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="small" type="primary" onClick={() => openEdit(record)}>
            Editar
          </Button>
          <Button size="small" danger onClick={() => handleRemove(record.uid)}>
            Excluir
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageWrapper id="page">
      <PageHeader
        title="Gerenciamento de FAQ"
        description="Gerencie as perguntas frequentes do aplicativo"
        type="private"
      />
      <DashboardMain className="dashboard-main">
        <Breadcrumb
          items={[
            { label: "Gerenciamento", link: "/manager" },
            { label: "FAQ", active: true },
          ]}
        />
        <DashboardSection className="dashboard-section">
          <ListHeader>
            <ModuleCardTitle className="module-card__title">
              Lista de FAQ
            </ModuleCardTitle>
            <Button type="primary" onClick={openCreate}>
              + Nova pergunta
            </Button>
          </ListHeader>
          <Table
            rowKey="uid"
            columns={columns}
            dataSource={items}
            pagination={{ hideOnSinglePage: true, pageSize: 15 }}
          />
        </DashboardSection>
      </DashboardMain>
      <Footer />

      <Modal
        open={visible}
        title={editing ? "Editar FAQ" : "Novo FAQ"}
        onCancel={() => setVisible(false)}
        onOk={handleSubmit}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="title"
            label="Pergunta"
            rules={[{ required: true, message: "Informe a pergunta" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Descrição"
            rules={[{ required: true, message: "Informe a descrição" }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="answer"
            label="Resposta"
            rules={[{ required: true, message: "Informe a resposta" }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </PageWrapper>
  );
};

export default FAQ;
